import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useOwnerService } from './OwnerServiceContext';
import { usePrototype } from './PrototypeContext';
import { reviewRoles } from '../data/applicationReview';
import { getDriver } from '../data/drivers';
import { can, isBlockingDuplicate } from '../utils/applicationQueue';
import { buildCngRecord, buildHistory, defaultReview, nowStamp, ownerInfo, reviewDocs } from '../utils/applicationRecords';
import type { Tone } from '../types/domain';
import type { OwnerApplication, OwnerScreen } from '../types/owner';
import type { AppReview, ApprovalRecord, DriverDecision, HistoryEntry, ReviewRole, RoleInfo, ValidationForm } from '../types/review';
interface CorrectionInput {
  reasons: string[];
  detail: string;
  instruction: string;
  comment: string;
}
interface RejectionInput {
  reason: string;
  detail: string;
  reference: string;
}
interface ReviewState {
  role: ReviewRole;
  setRole: (r: ReviewRole) => void;
  user: RoleInfo;
  driverIdsFor: (app: OwnerApplication) => string[];
  getReview: (app: OwnerApplication) => AppReview;
  updateReview: (app: OwnerApplication, patch: Partial<AppReview>) => void;
  getHistory: (app: OwnerApplication) => HistoryEntry[];
  addHistory: (app: OwnerApplication, action: string, status: string, tone: Tone, remarks?: string) => void;
  startReview: (app: OwnerApplication) => void;
  assignOfficer: (app: OwnerApplication, officer: string) => void;
  completeBrta: (app: OwnerApplication, ok: boolean) => void;
  saveValidation: (app: OwnerApplication, form: ValidationForm) => void;
  sendForApproval: (app: OwnerApplication, form: ValidationForm) => void;
  approve: (app: OwnerApplication, form: ApprovalRecord) => {
    ok: boolean;
    error?: string;
  };
  returnForCorrection: (app: OwnerApplication, input: CorrectionInput) => void;
  reject: (app: OwnerApplication, input: RejectionInput) => void;
  decideDriver: (app: OwnerApplication, driverId: string, decision: DriverDecision, note?: string) => void;
  resubmit: (appId: string, changes: Partial<Pick<OwnerApplication, 'chassis' | 'engine' | 'vehicleReg'>>) => void;
}
const ApplicationReviewContext = createContext<ReviewState | null>(null);
export function ApplicationReviewProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    applications,
    patchApplication,
    notify,
    addDelivery,
    log,
    ownerId,
    registrations
  } = useOwnerService();
  const {
    getCng,
    setStage,
    addCng,
    assignDriver,
    cngList
  } = usePrototype();
  const [role, setRole] = useState<ReviewRole>('যাচাই কর্মকর্তা');
  const [reviews, setReviews] = useState<Record<string, AppReview>>({});
  const [history, setHistory] = useState<Record<string, HistoryEntry[]>>({});
  const counter = useRef(300);
  const user = reviewRoles.find(r => r.role === role) ?? reviewRoles[1];
  const driverIdsFor = useCallback((app: OwnerApplication) => {
    if (app.vehicleType !== 'cng') return [];
    const linked = app.vehicleId ? getCng(app.vehicleId) : undefined;
    if (linked && linked.driverIds.length) return linked.driverIds;
    return app.ownerId === ownerId ? getCng('c1')?.driverIds ?? [] : [];
  }, [getCng, ownerId]);
  const baseReview = useCallback((app: OwnerApplication) => {
    const info = ownerInfo(app, registrations);
    return defaultReview(app, driverIdsFor(app), reviewDocs(app, info.nid).map(d => d.key));
  }, [registrations, driverIdsFor]);
  const getReview = useCallback((app: OwnerApplication) => reviews[app.id] ?? baseReview(app), [reviews, baseReview]);
  const updateReview = useCallback((app: OwnerApplication, patch: Partial<AppReview>) => setReviews(rs => ({
    ...rs,
    [app.id]: {
      ...(rs[app.id] ?? baseReview(app)),
      ...patch
    }
  })), [baseReview]);
  const getHistory = useCallback((app: OwnerApplication) => history[app.id] ?? buildHistory(app), [history]);
  const addHistory = useCallback((app: OwnerApplication, action: string, status: string, tone: Tone, remarks?: string, byOwner?: boolean) => {
    const who = byOwner ? `${app.ownerName} (গাড়ির মালিক — মোবাইল অ্যাপ)` : `${user.user} (${user.role})`;
    setHistory(h => ({
      ...h,
      [app.id]: [...(h[app.id] ?? buildHistory(app)), {
        time: nowStamp(),
        user: who,
        action,
        status,
        tone,
        remarks
      }]
    }));
    log({
      actor: byOwner ? app.ownerName : user.user,
      event: action,
      record: `${app.id} • ${app.vehicleReg}`,
      channel: byOwner ? 'মালিক অ্যাপ' : 'ওয়েব পোর্টাল',
      device: byOwner ? 'Android 14 • 103.98.12.44' : 'Chrome • 10.20.4.17'
    });
  }, [user, log]);
  const notifyOwner = useCallback((app: OwnerApplication, cat: string, title: string, body: string, important: boolean, template: string, action: OwnerScreen = 'tracking', vehicleId?: string) => {
    if (app.ownerId === ownerId) notify({
      cat,
      title,
      body,
      important,
      action,
      vehicleId: vehicleId ?? app.vehicleId,
      appId: app.id
    });
    addDelivery({
      template,
      recipient: `${app.ownerName} (${app.ownerMobile})`,
      channel: 'অ্যাপ + SMS',
      status: 'পৌঁছেছে'
    });
  }, [ownerId, notify, addDelivery]);
  const startReview = useCallback((app: OwnerApplication) => {
    if (app.stage > 1) return;
    patchApplication(app.id, {
      stage: 2,
      status: 'প্রক্রিয়াধীন',
      times: [...app.times, nowStamp()]
    });
    const r = getReview(app);
    if (!r.assignedOfficer) updateReview(app, {
      assignedOfficer: role === 'যাচাই কর্মকর্তা' ? user.user : 'মোঃ শফিকুল ইসলাম'
    });
    addHistory(app, 'প্রাথমিক যাচাই শুরু', 'যাচাই প্রক্রিয়াধীন', 'blue');
    notifyOwner(app, 'আবেদন আপডেট', 'আপনার আবেদন পর্যালোচনাধীন।', 'আপনার গাড়ির তথ্য ডিএমপি কর্তৃপক্ষ যাচাই করছে।', false, 'পর্যালোচনা শুরু');
  }, [patchApplication, getReview, updateReview, role, user, addHistory, notifyOwner]);
  const assignOfficer = useCallback((app: OwnerApplication, officer: string) => {
    updateReview(app, {
      assignedOfficer: officer
    });
    addHistory(app, `দায়িত্বপ্রাপ্ত কর্মকর্তা নির্ধারণ — ${officer}`, 'কর্মকর্তা নির্ধারিত', 'gray');
  }, [updateReview, addHistory]);
  const completeBrta = useCallback((app: OwnerApplication, ok: boolean) => {
    updateReview(app, {
      brtaDone: true
    });
    patchApplication(app.id, {
      brtaResult: ok ? 'মিলেছে' : 'অমিল',
      ...(app.stage < 3 && app.status !== 'সংশোধন প্রয়োজন' ? {
        stage: Math.max(app.stage, 3),
        status: 'প্রক্রিয়াধীন' as const,
        times: [...app.times, nowStamp()]
      } : {})
    });
    addHistory(app, ok ? 'BRTA তথ্য মিলেছে (সিমুলেটেড)' : 'BRTA তথ্যে অমিল চিহ্নিত (সিমুলেটেড)', ok ? 'তথ্য মিলেছে' : 'তথ্যের অমিল', ok ? 'green' : 'red');
  }, [updateReview, patchApplication, addHistory]);
  const saveValidation = useCallback((app: OwnerApplication, form: ValidationForm) => {
    updateReview(app, {
      validation: {
        ...form,
        saved: true
      }
    });
    addHistory(app, 'যাচাই সংরক্ষণ', form.result, form.result === 'তথ্য সঠিক' ? 'green' : 'orange', form.observation || undefined);
  }, [updateReview, addHistory]);
  const sendForApproval = useCallback((app: OwnerApplication, form: ValidationForm) => {
    updateReview(app, {
      validation: {
        ...form,
        saved: true
      }
    });
    patchApplication(app.id, {
      stage: 4,
      status: 'প্রক্রিয়াধীন',
      times: [...app.times.slice(0, 3), nowStamp()]
    });
    addHistory(app, 'যাচাই সম্পন্ন — অনুমোদনের জন্য পাঠানো হয়েছে', 'অনুমোদনের অপেক্ষায়', 'blue', form.observation || undefined);
  }, [updateReview, patchApplication, addHistory]);
  const approve = useCallback((app: OwnerApplication, form: ApprovalRecord) => {
    if (!can(role, 'approve')) return {
      ok: false,
      error: 'কেবল অনুমোদনকারী কর্মকর্তা চূড়ান্ত অনুমোদন দিতে পারেন'
    };
    if (app.stage < 4) return {
      ok: false,
      error: 'যাচাই সম্পন্ন হয়নি — আগে অনুমোদনের জন্য পাঠাতে হবে'
    };
    if (isBlockingDuplicate(app, applications)) return {
      ok: false,
      error: 'অমীমাংসিত ডুপ্লিকেট রেজিস্ট্রেশন — অনুমোদন করা যাবে না'
    };
    let vid = app.vehicleId ?? cngList.find(c => c.reg === app.vehicleReg)?.id;
    if (vid) {
      const cur = getCng(vid)?.stage ?? 0;
      if (cur < 4) setStage(vid, 4);
    } else {
      counter.current += 1;
      vid = `cown${counter.current}`;
      const prefix = app.vehicleType === 'cng' ? 'CNG' : 'MCL';
      addCng(buildCngRecord(app, vid, `DMP-${prefix}-${String(counter.current).padStart(6, '0')}`, ownerInfo(app, registrations)));
    }
    patchApplication(app.id, {
      stage: 5,
      status: 'অনুমোদিত',
      vehicleId: vid,
      correctionReason: undefined,
      times: [...app.times.slice(0, 4), nowStamp()]
    });
    updateReview(app, {
      approval: form
    });
    addHistory(app, 'অনুমোদিত', 'অনুমোদিত', 'green', form.comment || undefined);
    notifyOwner(app, 'গাড়ির অনুমোদন', 'অভিনন্দন! আপনার গাড়ির আবেদন অনুমোদিত হয়েছে।', `${app.vehicleReg} — আবেদন ${app.id}। ডিএমপি শীঘ্রই QR স্টিকার তৈরি করবে।`, false, 'অনুমোদন বার্তা', 'tracking', vid);
    return {
      ok: true
    };
  }, [role, applications, cngList, getCng, setStage, addCng, registrations, patchApplication, updateReview, addHistory, notifyOwner]);
  const returnForCorrection = useCallback((app: OwnerApplication, input: CorrectionInput) => {
    const r = getReview(app);
    const reason = `${input.reasons.join(', ')} — ${input.instruction || input.detail}`;
    patchApplication(app.id, {
      status: 'সংশোধন প্রয়োজন',
      correctionReason: reason,
      stage: Math.min(app.stage, 3)
    });
    updateReview(app, {
      corrections: [...r.corrections, {
        date: nowStamp(),
        ...input,
        by: `${user.user} (${user.role})`
      }],
      validation: {
        ...r.validation,
        saved: false
      }
    });
    addHistory(app, 'সংশোধনের জন্য ফেরত', 'সংশোধন প্রয়োজন', 'orange', `${input.reasons.join(', ')}। ${input.detail}`);
    notifyOwner(app, 'তথ্য সংশোধনের অনুরোধ', 'আপনার আবেদনে কিছু তথ্য সংশোধন প্রয়োজন। বিস্তারিত দেখুন।', `${app.vehicleReg} — ${input.instruction || input.detail}`, true, 'সংশোধনের অনুরোধ');
  }, [getReview, patchApplication, updateReview, user, addHistory, notifyOwner]);
  const reject = useCallback((app: OwnerApplication, input: RejectionInput) => {
    patchApplication(app.id, {
      status: 'বাতিল',
      correctionReason: `${input.reason} — ${input.detail}`
    });
    updateReview(app, {
      rejection: {
        ...input,
        by: `${user.user} (${user.role})`,
        date: nowStamp()
      }
    });
    addHistory(app, 'আবেদন বাতিল', 'বাতিল', 'red', `${input.reason}। ${input.detail}${input.reference ? ` • সূত্র: ${input.reference}` : ''}`);
    notifyOwner(app, 'আবেদন আপডেট', 'আপনার আবেদন অনুমোদিত হয়নি। বিস্তারিত কারণ দেখুন।', `${app.vehicleReg} — ${input.reason}`, true, 'বাতিল বার্তা');
  }, [patchApplication, updateReview, user, addHistory, notifyOwner]);
  const decideDriver = useCallback((app: OwnerApplication, driverId: string, decision: DriverDecision, note?: string) => {
    const r = getReview(app);
    updateReview(app, {
      drivers: {
        ...r.drivers,
        [driverId]: decision
      },
      driverNotes: {
        ...r.driverNotes,
        [driverId]: note ?? ''
      }
    });
    if (decision === 'approved' && app.vehicleId) assignDriver(app.vehicleId, driverId);
    const name = getDriver(driverId)?.name ?? driverId;
    addHistory(app, `চালক সংযুক্তি — ${name}`, decision === 'approved' ? 'অনুমোদিত' : 'সংশোধনের জন্য ফেরত', decision === 'approved' ? 'green' : 'orange', note || undefined);
    if (decision === 'approved') notifyOwner(app, 'চালক অনুমোদন', `চালক ${name} অনুমোদিত হয়েছেন।`, `${app.vehicleReg} গাড়ির অনুমোদিত চালক তালিকায় যুক্ত হয়েছেন।`, false, 'চালক অনুমোদন', 'drivers');
  }, [getReview, updateReview, assignDriver, addHistory, notifyOwner]);
  const resubmit = useCallback((appId: string, changes: Partial<Pick<OwnerApplication, 'chassis' | 'engine' | 'vehicleReg'>>) => {
    const app = applications.find(a => a.id === appId);
    if (!app) return;
    const r = getReview(app);
    patchApplication(appId, {
      ...changes,
      status: 'প্রক্রিয়াধীন',
      stage: 2,
      brtaResult: 'অনুপলব্ধ',
      correctionReason: undefined
    });
    updateReview(app, {
      vehicleResult: undefined,
      vehicleChecks: r.vehicleChecks.map(() => false),
      brtaDone: false,
      brtaFlags: [],
      docs: Object.fromEntries(Object.entries(r.docs).map(([k, s]) => [k, s === 'verified' ? 'verified' : 'pending'])),
      validation: {
        ...r.validation,
        saved: false
      }
    });
    const summary = Object.entries(changes).filter(([k, v]) => v && v !== app[k as keyof typeof changes]).map(([k, v]) => `${k === 'chassis' ? 'চেসিস' : k === 'engine' ? 'ইঞ্জিন' : 'রেজিস্ট্রেশন'}: ${app[k as keyof typeof changes]} → ${v}`).join(' • ');
    addHistory(app, 'সংশোধিত তথ্য পুনরায় জমা — যাচাই সারিতে ফেরত', 'যাচাই প্রক্রিয়াধীন', 'blue', summary || 'সংশোধিত ডকুমেন্ট সংযুক্ত', true);
  }, [applications, getReview, patchApplication, updateReview, addHistory]);
  const value: ReviewState = {
    role,
    setRole,
    user,
    driverIdsFor,
    getReview,
    updateReview,
    getHistory,
    addHistory,
    startReview,
    assignOfficer,
    completeBrta,
    saveValidation,
    sendForApproval,
    approve,
    returnForCorrection,
    reject,
    decideDriver,
    resubmit
  };
  return <ApplicationReviewContext.Provider value={value} data-id="element-2242" data-mp="2wl5bift0k673">{children}</ApplicationReviewContext.Provider>;
}
export function useApplicationReview(): ReviewState {
  const ctx = useContext(ApplicationReviewContext);
  if (!ctx) throw new Error('useApplicationReview must be used inside ApplicationReviewProvider');
  return ctx;
}